import React, { useEffect, useState } from "react";
import { CheckCircle2, CreditCard, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "./api";
import { formatISK } from "./currency";

/**
 * Post-booking dialog. Offers an OPTIONAL Stripe deposit.
 * If Stripe is disabled server-side (RESEND/Stripe key missing), we simply show the
 * success summary without a pay button — admin will still confirm manually.
 */
export function BookingSuccessDialog({ booking, onClose }) {
  const [cfg, setCfg] = useState(null); // null=loading, {enabled, amount, currency}
  const [paying, setPaying] = useState(false);
  const canPayOnline = booking?.invoice_mode === "online_card" && booking?.status === "pending_payment" && !booking?.quote_required;

  useEffect(() => {
    api.get("/payments/config").then((r) => setCfg(r.data)).catch(() => setCfg({ enabled: false }));
  }, []);

  const payDeposit = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      // Remember which booking we're paying for so the success page can refresh
      sessionStorage.setItem("icefleet_pay_booking", booking.id);
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start payment");
      setPaying(false);
    }
  };

  const amount = cfg?.amount ? formatAmount(cfg.amount, cfg.currency) : null;

  return (
    <div
      data-testid="booking-success-dialog"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-slate-900/70 p-0 sm:p-6"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-2xl sm:rounded-xl bg-white p-6 space-y-4 max-h-[92vh] overflow-auto"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 shrink-0">
              <CheckCircle2 size={18} />
            </span>
            <div>
              <h3 className="font-heading text-xl font-semibold">Booking request sent</h3>
              <p className="text-sm text-slate-600 mt-1">
                Thanks {booking.customer_name}. We'll confirm by email ({booking.email}) shortly.
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="close" className="text-slate-500"><X size={18} /></button>
        </div>

        <div className="rounded-md bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 space-y-1">
          <p><span className="font-semibold text-slate-900 capitalize">{booking.service_type}</span> · request pending confirmation</p>
          <p>{new Date(booking.start_at).toLocaleString()} → {new Date(booking.end_at).toLocaleString()}</p>
          {booking.pricing_mode === "fixed_price" && booking.fixed_price_isk ? (
            <p className="pt-1">
              Fixed price:{" "}
              <span className="font-semibold text-slate-900 tabular-nums">
                {formatISK(booking.fixed_price_isk)}
              </span>
            </p>
          ) : null}
        </div>
        <p className="text-[11px] leading-5 text-slate-500" data-testid="booking-legal-trust-note">
          Payments and invoices are processed by FONDO ehf., operator of IceFleet.
        </p>

        {booking.quote_required && (
          <div
            data-testid="quote-required-notice"
            className="rounded-md border border-amber-200 bg-amber-50/60 p-4 text-sm text-slate-800"
          >
            This route requires manual confirmation. We'll review the details and confirm the price shortly.
          </div>
        )}

        {cfg?.enabled && canPayOnline && (
          <div className="rounded-md border border-sky-200 bg-sky-50/60 p-4 space-y-3">
            <div className="flex items-center gap-2 text-sky-900">
              <CreditCard size={16} />
              <p className="text-sm font-semibold">Optional — secure your spot</p>
            </div>
            <p className="text-xs text-sky-900/80">
              Pay a small {amount} deposit now to lock in your booking. Deposit is refunded or applied to the
              final invoice. You can also skip — we'll simply email you to confirm and invoice manually.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={payDeposit}
                disabled={paying}
                data-testid="pay-deposit-button"
                className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
              >
                <CreditCard size={14} /> {paying ? "Redirecting…" : `Pay ${amount} deposit`}
              </button>
              <button
                type="button"
                onClick={onClose}
                data-testid="skip-deposit-button"
                className="flex-1 rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Skip — confirm by email
              </button>
            </div>
            <p
              data-testid="rapyd-trust-note"
              className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 pt-1"
            >
              <svg viewBox="0 0 20 20" className="w-3.5 h-3.5 fill-current text-slate-400" aria-hidden="true">
                <path d="M10 1l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V4l7-3zm0 2.2L5 5.3v3.7c0 3.3 2.1 6.3 5 7.5 2.9-1.2 5-4.2 5-7.5V5.3l-5-2.1z"/>
              </svg>
              Secure payment powered by Rapyd
            </p>
          </div>
        )}

        {(cfg && !cfg.enabled) || !canPayOnline ? (
          <button
            type="button"
            onClick={onClose}
            className="w-full rounded-md bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Close
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function formatAmount(amount, currency = "eur") {
  if (String(currency || "").toLowerCase() === "isk") {
    return formatISK(amount, { empty: "0 kr" });
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "eur").toUpperCase(),
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount} ${String(currency || "eur").toUpperCase()}`;
  }
}
